import React from 'react';
import { X, GitBranch } from 'lucide-react';
import ApplicationForm, { ApplicationFormSubmitMeta } from './ApplicationForm';
import PostHogService, { ApplicationEvent } from '../../posthog';

interface CreateApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onCreated: (application: { id: string }) => void;
}

const CreateApplicationModal: React.FC<CreateApplicationModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  onCreated,
}) => {
  if (!isOpen) return null;

  const handleSuccess = (application: { id: string }, meta: ApplicationFormSubmitMeta) => {
    PostHogService.track(ApplicationEvent.CREATED, {
      organization_id: organizationId,
      application_id: application.id,
      repo_count: meta.repoCount,
      repo_providers: meta.repoProviders,
      private_repo_count: meta.privateRepoCount,
      has_description: meta.hasDescription,
      source: 'manual',
    });
    onCreated(application);
    onClose();
  };

  const handleError = (err: unknown, meta: ApplicationFormSubmitMeta) => {
    const error = err as { data?: { detail?: string }; message?: string };
    const reason = error?.data?.detail || error?.message || 'unknown_error';
    PostHogService.track(ApplicationEvent.CREATE_FAILED, {
      organization_id: organizationId,
      reason,
      repo_count: meta.repoCount,
      repo_providers: meta.repoProviders,
      private_repo_count: meta.privateRepoCount,
      has_description: meta.hasDescription,
      source: 'manual',
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-ui-border-primary shrink-0">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-ui-text-secondary" />
              <h2 className="text-lg font-semibold text-ui-text-primary">
                Add repository
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
            >
              <X className="w-5 h-5 text-ui-text-tertiary" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <ApplicationForm
              organizationId={organizationId}
              variant="single_repo"
              submitLabel="Add repository"
              onSuccess={handleSuccess}
              onError={handleError}
              onCancel={onClose}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateApplicationModal;
